// [2026-09-06] ADDED — the ONE definition of "which service did this call belong to".
//
// WHY THIS FILE EXISTS AT ALL
// ---------------------------
// A customer spends credits on exactly two products, and until now the dashboard showed only their
// sum. "You used 340 credits" does not answer the question a customer actually asks, which is
// "which of the two things I am paying for is costing me that, and is either of them failing?"
//
// The split is REAL and already exists server-side: app/core/credits.py declares SURFACE_API (the
// server-to-server video analysis API) and SURFACE_SDK (the browser proctoring SDK), and the credit
// summary returns `api_used` / `sdk_used` against those. This module is the CLIENT-side counterpart
// for the TRAFFIC side of the same split — mapping each metered endpoint onto the same two
// services, so the calls-and-errors figures line up with the credits figures rather than being a
// second, differently-drawn boundary.
//
// WHY A PREFIX TABLE AND NOT A REGEX PER SCREEN
// ---------------------------------------------
// Both the Credits screen and the Analytics table need this classification. Two copies is two
// chances for one screen to count /sdk/v1/evidence as video processing and quietly disagree with
// the other about the same day's traffic. One table, imported twice.
//
// THE ENDPOINT STRINGS ARE THE ONES THE BACKEND ACTUALLY RECORDS, not the ones in the router
// decorators — those differ. Verified against the four `usage.record` call sites:
//   /proctoring/video/analyze     — app/core/video_processing_proxy.py  (BILLED, SURFACE_API)
//   /proctoring/browser/sessions  — app/modules/browser/route.py:728    (BILLED, SURFACE_SDK)
//   /sdk/v1/events                — app/modules/browser/route.py:1226   (not billed)
//   /sdk/v1/evidence              — app/modules/browser/route.py:1265   (not billed)
//
// UNRECOGNISED ENDPOINTS ARE NOT DISCARDED. Anything that matches no prefix falls into `other`,
// which the screens render as its own row rather than dropping. An endpoint added to the backend
// tomorrow then shows up as uncategorised traffic — visibly missing a home, which is what prompts
// someone to add it here. Silently omitting it would make the per-service totals quietly stop
// summing to the overall total, and nobody would notice.

export const SERVICE_VIDEO = 'video'
export const SERVICE_SDK = 'sdk'
export const SERVICE_OTHER = 'other'

// Longest-prefix-wins is NOT needed here (no prefix is a prefix of another), but the order is still
// fixed and explicit so behaviour cannot change with object-key iteration order.
const PREFIXES = [
  { prefix: '/proctoring/video/', service: SERVICE_VIDEO },
  { prefix: '/proctoring/browser/', service: SERVICE_SDK },
  { prefix: '/sdk/v1/', service: SERVICE_SDK },
]

/** Which service a recorded endpoint belongs to. Never throws, never returns undefined. */
export function serviceOf(endpoint) {
  const path = typeof endpoint === 'string' ? endpoint : ''
  for (const entry of PREFIXES) {
    if (path.startsWith(entry.prefix)) return entry.service
  }
  return SERVICE_OTHER
}

// The display metadata for each service, kept beside the classification so a screen never has to
// hardcode a label. `creditField` names the key on the /me/credits summary that carries this
// service's spend (app/core/credits.py SURFACE_USED_FIELDS) — `other` has none, because traffic
// with no service also has no billing surface, and inventing one would be a lie about money.
export const SERVICES = [
  {
    key: SERVICE_VIDEO,
    label: 'Proctoring',
    blurb: 'Recorded interviews queued for analysis',
    creditField: 'api_used',
  },
  {
    key: SERVICE_SDK,
    label: 'Browser SDK',
    blurb: 'Live candidate sessions and telemetry',
    creditField: 'sdk_used',
  },
  {
    key: SERVICE_OTHER,
    label: 'Other',
    blurb: 'Calls not attributed to a billed service',
    creditField: null,
  },
]

/** Roll a /me/analytics `endpoints` array up into per-service totals.
 *
 *  SUMS THE COUNTS, RE-DERIVES THE RATE. `error_rate` on each endpoint row is already a ratio, and
 *  averaging ratios weights a 2-call endpoint the same as a 2000-call one — which is how a service
 *  with one failing healthcheck reads as 50% broken. The rate here is recomputed from the summed
 *  numerator and denominator, so it is the true rate across the service.
 *
 *  `credits` is summed from the same per-endpoint counter the billing tiles use, so the traffic
 *  table and the credit tiles cannot disagree.
 *
 *  Returns a row for EVERY service in SERVICES, including ones with no traffic — a service missing
 *  from the screen reads as "not offered", when it means "not used this window", and those are
 *  different facts. `other` is the exception: it is omitted when empty, because a permanently
 *  visible "Other: 0" row is noise on the normal case.
 */
export function rollUpByService(endpoints) {
  const blank = () => ({
    total: 0, success: 0, client_error: 0, server_error: 0, credits: 0,
    endpoints: [], latency_weighted: 0, latency_calls: 0,
  })
  const acc = new Map(SERVICES.map((s) => [s.key, blank()]))

  for (const row of endpoints || []) {
    const bucket = acc.get(serviceOf(row.endpoint))
    if (!bucket) continue
    const total = Number(row.total) || 0
    bucket.total += total
    bucket.success += Number(row.success) || 0
    bucket.client_error += Number(row.client_error) || 0
    bucket.server_error += Number(row.server_error) || 0
    bucket.credits += Number(row.credits) || 0
    bucket.endpoints.push(row)
    // Call-weighted, for the same reason the error rate is: an unweighted mean of per-endpoint
    // averages is not the average latency of the service.
    if (row.avg_latency_ms != null && total > 0) {
      bucket.latency_weighted += Number(row.avg_latency_ms) * total
      bucket.latency_calls += total
    }
  }

  return SERVICES
    .map((meta) => {
      const b = acc.get(meta.key)
      const errors = b.client_error + b.server_error
      return {
        ...meta,
        total: b.total,
        success: b.success,
        client_error: b.client_error,
        server_error: b.server_error,
        errors,
        // Zero calls means the rate is UNKNOWN, not zero. `null` renders as "—"; a literal 0%
        // would claim a clean record for a service that was never exercised.
        error_rate: b.total > 0 ? errors / b.total : null,
        credits: Math.round(b.credits * 1000) / 1000,
        avg_latency_ms: b.latency_calls > 0
          ? Math.round((b.latency_weighted / b.latency_calls) * 10) / 10
          : null,
        endpoints: b.endpoints.slice().sort((a, z) => (z.total || 0) - (a.total || 0)),
      }
    })
    .filter((row) => row.key !== SERVICE_OTHER || row.total > 0)
}
